/**
 * ParticleEmitter — lightweight pool of physics particles drawn into a
 * single shared `Graphics` object per frame.
 *
 * NOT Phaser's built-in `ParticleEmitterManager` (which is heavy and uses
 * sprite atlases). This is the same hand-rolled pattern that horse-dash,
 * puff-up, tap-tug, balloon-pop, fruit-grab, and bird-race each duplicated:
 * an array of `{ x, y, vx, vy, life, ttl, color, size, shape, gravity, ... }`,
 * integrated each tick, rendered as primitive shapes via `Graphics.fill*`.
 *
 * Built-in shapes: 'circle', 'cloud', 'sparkle', 'spark'. For custom looks,
 * pass `shape: 'custom'` and provide a `draw(g, particle, alpha)` callback
 * on the particle itself, or set a global custom renderer via
 * `emitter.setRenderer(fn)`.
 *
 * Built-in presets: `burst`, `cloud`, `sparks`, `sparkle`, `fireworks`.
 * These cover the union of patterns used by current games. Each preset
 * pushes N particles in a single call.
 *
 * Usage:
 *   import { ParticleEmitter } from '@platform/sdk';
 *
 *   start() {
 *     this._fx = new ParticleEmitter({ scene: this, depth: 14 });
 *   }
 *
 *   update(_t, delta) {
 *     this._fx.update(delta / 1000);
 *   }
 *
 *   _onPlayerScored(x, y, color) {
 *     this._fx.burst(x, y, { color, count: 18, speed: [140, 220], ttl: 0.6 });
 *     this._fx.sparkle(x, y, { count: 10, ttl: 0.55 });
 *   }
 */
export class ParticleEmitter {
  /**
   * @param {object} opts
   * @param {Phaser.Scene} opts.scene
   * @param {number} [opts.depth=10]    Phaser display depth for the Graphics layer
   * @param {number} [opts.maxParticles=400]  hard cap to keep slow devices happy
   */
  constructor(opts) {
    this.scene = opts.scene;
    this.maxParticles = opts.maxParticles || 400;
    this._particles = [];
    this._renderer = null;

    this.graphics = opts.scene.add.graphics();
    if (opts.depth != null) this.graphics.setDepth(opts.depth);
  }

  /** Override the renderer for `shape: 'custom'` particles. */
  setRenderer(fn) {
    this._renderer = typeof fn === 'function' ? fn : null;
  }

  /**
   * Push a single particle. Use this when none of the presets fit and you
   * want full control. Particle properties:
   *   { x, y, vx, vy,
   *     color (hex), size,
   *     shape ('circle' | 'cloud' | 'sparkle' | 'spark' | 'custom'),
   *     ttl (seconds),
   *     gravity (px/sec^2, optional, default 0),
   *     rot, vrot (radians, optional),
   *     draw (custom shape only) }
   */
  emit(p) {
    if (this._particles.length >= this.maxParticles) return;
    if (p.life == null) p.life = 0;
    if (p.gravity == null) p.gravity = 0;
    if (p.rot == null) p.rot = 0;
    if (p.vrot == null) p.vrot = 0;
    this._particles.push(p);
  }

  /**
   * Per-frame integration + render. Call from `update(_t, delta)` with
   * `delta / 1000`.
   */
  update(dt) {
    const g = this.graphics;
    g.clear();
    const arr = this._particles;
    for (let i = arr.length - 1; i >= 0; i--) {
      const p = arr[i];
      p.life += dt;
      if (p.life >= p.ttl) { arr.splice(i, 1); continue; }
      p.vy += p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vrot * dt;
      const a = 1 - (p.life / p.ttl);
      this._draw(g, p, a);
    }
  }

  _draw(g, p, a) {
    switch (p.shape) {
      case 'circle':
        g.fillStyle(p.color, a);
        g.fillCircle(p.x, p.y, p.size * (0.3 + a * 0.7));
        break;
      case 'cloud':
        g.fillStyle(p.color, a * 0.55);
        g.fillCircle(p.x, p.y, p.size * (1 + p.life * 0.8));
        break;
      case 'sparkle':
        g.fillStyle(0xffffff, a);
        g.fillCircle(p.x, p.y, p.size * a);
        g.lineStyle(2 * a, 0xffffff, a * 0.8);
        g.strokeCircle(p.x, p.y, p.size * 2 + (1 - a) * 5);
        break;
      case 'spark':
        // Bright streak — small circle + trailing line.
        g.fillStyle(p.color, a);
        g.fillCircle(p.x, p.y, p.size * 0.8);
        g.lineStyle(2 * a, p.color, a);
        g.beginPath();
        g.moveTo(p.x, p.y);
        g.lineTo(p.x - p.vx * 0.04, p.y - p.vy * 0.04);
        g.strokePath();
        break;
      case 'custom':
        if (p.draw) p.draw(g, p, a);
        else if (this._renderer) this._renderer(g, p, a);
        break;
      default:
        g.fillStyle(p.color, a);
        g.fillCircle(p.x, p.y, p.size);
    }
  }

  /** Remove every active particle and clear the canvas immediately. */
  clear() {
    this._particles.length = 0;
    if (this.graphics) this.graphics.clear();
  }

  // ── Presets ────────────────────────────────────────────────────────

  /**
   * Radial burst — N particles ejected outward at evenly-spaced angles.
   * The "score-popped" effect from horse-dash / puff-up / tap-tug.
   * @param {number} x
   * @param {number} y
   * @param {object} [opts]
   * @param {number} [opts.count=18]
   * @param {number} [opts.color=0xfacc15]
   * @param {[number, number]} [opts.speed=[140, 220]]   [min, max] px/s
   * @param {number} [opts.size=4]                       base radius
   * @param {number} [opts.ttl=0.6]                      seconds
   * @param {number} [opts.gravity=60]
   */
  burst(x, y, opts = {}) {
    const count = opts.count || 18;
    const color = opts.color != null ? opts.color : 0xfacc15;
    const speedMin = opts.speed ? opts.speed[0] : 140;
    const speedSpan = opts.speed ? (opts.speed[1] - opts.speed[0]) : 80;
    const size = opts.size || 4;
    const ttl = opts.ttl || 0.6;
    const gravity = opts.gravity != null ? opts.gravity : 60;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2;
      const sp = speedMin + Math.random() * speedSpan;
      this.emit({
        x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        color, shape: 'circle',
        size: size + Math.random() * 3,
        ttl, gravity
      });
    }
  }

  /**
   * Cloud puff — soft expanding circles. Used for dust at feet, smoke,
   * "feet hitting the ground" type juice.
   */
  cloud(x, y, opts = {}) {
    const count = opts.count || 4;
    const palette = opts.palette || [0xfde68a, 0xfbbf24, 0xfef3c7];
    const speedMin = opts.speed ? opts.speed[0] : 30;
    const speedSpan = opts.speed ? (opts.speed[1] - opts.speed[0]) : 50;
    const angleBase = opts.angleBase != null ? opts.angleBase : Math.PI;
    const angleSpread = opts.angleSpread != null ? opts.angleSpread : 0.7;
    const size = opts.size || 6;
    const ttl = opts.ttl || 0.55;
    const gravity = opts.gravity != null ? opts.gravity : -25;
    for (let i = 0; i < count; i++) {
      const a = angleBase + (Math.random() - 0.5) * angleSpread;
      const sp = speedMin + Math.random() * speedSpan;
      this.emit({
        x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 10,
        color: palette[Math.floor(Math.random() * palette.length)],
        shape: 'cloud',
        size: size + Math.random() * 4,
        ttl, gravity
      });
    }
  }

  /**
   * Spark spray — bright streaks shooting upward. Used for fire, impact
   * sparks, "this hit was great" feedback.
   */
  sparks(x, y, opts = {}) {
    const count = opts.count || 4;
    const color = opts.color != null ? opts.color : 0xfacc15;
    const speedMin = opts.speed ? opts.speed[0] : 90;
    const speedSpan = opts.speed ? (opts.speed[1] - opts.speed[0]) : 70;
    const angleBase = opts.angleBase != null ? opts.angleBase : -Math.PI / 2;
    const angleSpread = opts.angleSpread != null ? opts.angleSpread : Math.PI;
    const ttl = opts.ttl || 0.5;
    const gravity = opts.gravity != null ? opts.gravity : 80;
    for (let i = 0; i < count; i++) {
      const a = angleBase + (Math.random() - 0.5) * angleSpread;
      const sp = speedMin + Math.random() * speedSpan;
      this.emit({
        x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 30,
        color, shape: 'spark',
        size: 3 + Math.random() * 2,
        ttl, gravity
      });
    }
  }

  /**
   * Sparkle spray — small white twinkles. Pairs nicely with `burst` for
   * high-tier combos.
   */
  sparkle(x, y, opts = {}) {
    const count = opts.count || 10;
    const speedMin = opts.speed ? opts.speed[0] : 60;
    const speedSpan = opts.speed ? (opts.speed[1] - opts.speed[0]) : 90;
    const ttl = opts.ttl || 0.55;
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = speedMin + Math.random() * speedSpan;
      this.emit({
        x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 30,
        color: 0xffffff,
        shape: 'sparkle',
        size: 3,
        ttl, gravity: 0
      });
    }
  }

  /**
   * Fireworks — N delayed bursts at random positions across the upper
   * portion of the scene. Used for endgame celebration.
   * Returns nothing — bursts schedule themselves on the scene's time queue.
   */
  fireworks(opts = {}) {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const count = opts.count || 5;
    const palette = opts.palette || [0xef4444, 0xfacc15, 0x3b82f6, 0x10b981, 0xec4899, 0x8b5cf6];
    const burstSize = opts.burstSize || 38;
    const stagger = opts.stagger != null ? opts.stagger : 320;
    const onPop = opts.onPop || null;
    for (let b = 0; b < count; b++) {
      const cx = W * (0.15 + Math.random() * 0.7);
      const cy = H * (0.20 + Math.random() * 0.25);
      const color = palette[b % palette.length];
      this.scene.time.delayedCall(b * stagger, () => {
        for (let i = 0; i < burstSize; i++) {
          const a = (i / burstSize) * Math.PI * 2;
          const sp = 200 + Math.random() * 120;
          this.emit({
            x: cx, y: cy,
            vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
            color, shape: 'circle',
            size: 4, ttl: 1.4, gravity: 180
          });
        }
        if (onPop) try { onPop(b); } catch (_) { /* ignore */ }
      });
    }
  }

  /** Tear down the underlying Graphics object. */
  destroy() {
    this._particles.length = 0;
    if (this.graphics) this.graphics.destroy();
    this.graphics = null;
    this.scene = null;
  }
}
